import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileText, PenTool, Loader2, CheckCircle, Plus, Trash2, ChevronDown, ChevronUp, FilePlus2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const diffColors = {
  easy: "bg-emerald-500/20 text-emerald-400",
  medium: "bg-amber-500/20 text-amber-400",
  hard: "bg-red-500/20 text-red-400",
};

const TOPICS = [
  { id: "polity", label: "Indian Polity & Governance" },
  { id: "economy", label: "Economy" },
  { id: "environment", label: "Environment & Ecology" },
  { id: "history", label: "History" },
  { id: "geography", label: "Geography" },
  { id: "science", label: "Science & Technology" },
  { id: "culture", label: "Art & Culture" },
  { id: "current_affairs", label: "Current Affairs" },
];

const SUBTOPICS = {
  polity: ["fundamental_rights", "dpsp", "parliament", "federalism", "judiciary", "governance", "constitutional_bodies", "local_governance", "elections"],
  economy: ["monetary_policy", "fiscal_policy", "banking", "agriculture_eco", "industry", "poverty", "trade", "schemes"],
  environment: ["ecology", "biodiversity", "climate_change", "conservation", "pollution", "national_parks", "env_treaties"],
  history: ["ancient_india", "medieval_india", "modern_india", "freedom_struggle", "post_independence"],
  geography: ["physical_geo", "indian_geo", "world_geo", "climatology", "oceanography"],
  science: ["space_tech", "defense_tech", "biotechnology", "it_comm", "basic_science"],
  culture: ["architecture", "performing_arts", "religion", "literature"],
  current_affairs: ["national_ca", "international_ca", "economy_ca", "science_ca"],
};

const topicLabels = {
  polity: "Polity", economy: "Economy", environment: "Environment",
  history: "History", geography: "Geography", science: "Science",
  culture: "Culture", current_affairs: "Current Affairs",
};

const EMPTY_QUESTION = {
  question_text: "",
  question_type: "standard",
  options: { A: "", B: "", C: "", D: "" },
  correct_answer: "",
  explanation: "",
  difficulty_level: "hard",
  topic_id: "polity",
  subtopic_id: "fundamental_rights",
  is_current_affair: false,
  source_citations: [],
  is_relevant: true,
};

function ExtractedQuestion({ q, index, onToggle, selected }) {
  const isDuplicate = q.is_duplicate;
  const isIrrelevant = q.is_relevant === false;
  const disabled = isDuplicate || isIrrelevant;

  return (
    <div
      data-testid={`extracted-q-${index}`}
      className={`p-4 border rounded-sm transition-all ${
        disabled ? "border-border/50 opacity-50" : selected ? "border-primary/50 bg-primary/5" : "border-border"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="font-mono text-xs text-muted-foreground">Q{index + 1}</span>
            <Badge variant="outline" className={`text-[10px] ${diffColors[q.difficulty_level] || diffColors.medium}`}>
              {q.difficulty_level || "medium"}
            </Badge>
            <Badge variant="outline" className="text-[10px]">
              {topicLabels[q.topic_id] || q.topic_id}
            </Badge>
            {isDuplicate && (
              <Badge className="bg-amber-500/20 text-amber-400 text-[10px] border-0">Duplicate</Badge>
            )}
            {isIrrelevant && (
              <Badge className="bg-red-500/20 text-red-400 text-[10px] border-0">Not UPSC Level</Badge>
            )}
            {q.relevance_score !== undefined && (
              <Badge variant="outline" className={`text-[10px] ${
                q.relevance_score >= 7 ? "text-emerald-400 border-emerald-500/30" :
                q.relevance_score >= 4 ? "text-amber-400 border-amber-500/30" :
                "text-red-400 border-red-500/30"
              }`}>
                UPSC: {q.relevance_score}/10
              </Badge>
            )}
            {q.correct_answer && (
              <Badge variant="outline" className="text-[10px] text-emerald-400 border-emerald-500/30">
                Ans: {q.correct_answer}
              </Badge>
            )}
            {q.has_answer === true && (
              <Badge variant="outline" className="text-[10px] text-blue-400 border-blue-500/30">
                Coaching Answer
              </Badge>
            )}
            {q.has_explanation === true && (
              <Badge variant="outline" className="text-[10px] text-purple-400 border-purple-500/30">
                Has Explanation
              </Badge>
            )}
            {q.has_answer === false && (
              <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/30">
                No Answer
              </Badge>
            )}
            {q.has_truncated_options && (
              <Badge variant="outline" className="text-[10px] text-red-400 border-red-500/30">
                Truncated Options
              </Badge>
            )}
          </div>
          <p className="text-sm whitespace-pre-line leading-relaxed">{q.question_text}</p>
          {q.options && (
            <div className="mt-2 space-y-1">
              {Object.entries(q.options).map(([key, val]) => (
                <p key={key} className="text-xs text-muted-foreground">
                  <span className={`font-mono ${key === q.correct_answer ? "text-emerald-400 font-semibold" : ""}`}>({key})</span> {val}
                </p>
              ))}
            </div>
          )}
          {q.irrelevance_reason && (
            <p className="text-xs text-red-400 mt-2">Reason: {q.irrelevance_reason}</p>
          )}
          {q.explanation && (
            <details className="mt-2">
              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-primary transition-colors">
                View Explanation
              </summary>
              <p className="text-xs text-muted-foreground mt-1 p-2 bg-secondary/30 rounded-sm whitespace-pre-line leading-relaxed max-h-40 overflow-y-auto">
                {q.explanation.substring(0, 500)}{q.explanation.length > 500 ? "..." : ""}
              </p>
            </details>
          )}
        </div>
        {!disabled && (
          <Switch
            data-testid={`toggle-q-${index}`}
            checked={selected}
            onCheckedChange={() => onToggle(index)}
          />
        )}
      </div>
    </div>
  );
}

function ManualQuestionForm({ question, index, onChange, onRemove }) {
  const [expanded, setExpanded] = useState(true);
  const subs = SUBTOPICS[question.topic_id] || [];

  const update = (field, value) => {
    onChange(index, { ...question, [field]: value });
  };

  const updateOption = (key, value) => {
    onChange(index, { ...question, options: { ...question.options, [key]: value } });
  };

  return (
    <Card className="bg-card border-border" data-testid={`manual-q-${index}`}>
      <button
        className="w-full flex items-center justify-between p-4 text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-muted-foreground">Q{index + 1}</span>
          <Badge variant="outline" className={`text-[10px] ${diffColors[question.difficulty_level]}`}>
            {question.difficulty_level}
          </Badge>
          {question.question_text ? (
            <span className="text-sm truncate max-w-[400px]">{question.question_text.substring(0, 60)}...</span>
          ) : (
            <span className="text-sm text-muted-foreground italic">Empty question</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost" size="sm"
            onClick={(e) => { e.stopPropagation(); onRemove(index); }}
            className="text-red-400 hover:text-red-300 h-7 w-7 p-0"
            data-testid={`remove-q-${index}`}
          >
            <Trash2 size={14} />
          </Button>
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
      </button>

      {expanded && (
        <CardContent className="pt-0 space-y-4 border-t border-border mt-0 pt-4">
          {/* Question Text */}
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Question Text</label>
            <Textarea
              data-testid={`q-text-${index}`}
              placeholder="Enter the question text. For multi-statement questions, include all statements..."
              value={question.question_text}
              onChange={e => update("question_text", e.target.value)}
              className="bg-background border-border min-h-[100px] text-sm"
            />
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {["A", "B", "C", "D"].map(key => (
              <div key={key}>
                <label className="text-xs text-muted-foreground mb-1 block">Option {key}</label>
                <Input
                  data-testid={`q-opt-${key}-${index}`}
                  placeholder={`Option ${key}...`}
                  value={question.options[key]}
                  onChange={e => updateOption(key, e.target.value)}
                  className="bg-background border-border text-sm"
                />
              </div>
            ))}
          </div>

          {/* Correct Answer + Difficulty + Topic */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Correct Answer</label>
              <Select value={question.correct_answer} onValueChange={v => update("correct_answer", v)}>
                <SelectTrigger className="bg-background border-border" data-testid={`q-ans-${index}`}>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {["A", "B", "C", "D"].map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Difficulty</label>
              <Select value={question.difficulty_level} onValueChange={v => update("difficulty_level", v)}>
                <SelectTrigger className="bg-background border-border" data-testid={`q-diff-${index}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Topic</label>
              <Select value={question.topic_id} onValueChange={v => {
                const firstSub = (SUBTOPICS[v] || [])[0] || "";
                onChange(index, { ...question, topic_id: v, subtopic_id: firstSub });
              }}>
                <SelectTrigger className="bg-background border-border" data-testid={`q-topic-${index}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TOPICS.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Subtopic</label>
              <Select value={question.subtopic_id} onValueChange={v => update("subtopic_id", v)}>
                <SelectTrigger className="bg-background border-border" data-testid={`q-sub-${index}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {subs.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Explanation */}
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Explanation (optional)</label>
            <Textarea
              data-testid={`q-explain-${index}`}
              placeholder="Explain the correct answer with source citation..."
              value={question.explanation}
              onChange={e => update("explanation", e.target.value)}
              className="bg-background border-border min-h-[60px] text-sm"
            />
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export default function QuestionSeeder() {
  const [mode, setMode] = useState(null); // "text" | "file" | "manual" | "pair"
  const [pairType, setPairType] = useState("separate"); // "separate" | "combined"
  const [textInput, setTextInput] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [file, setFile] = useState(null);
  const [qpFile, setQpFile] = useState(null);
  const [solFile, setSolFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);

  // Manual entry state
  const [manualQuestions, setManualQuestions] = useState([{ ...EMPTY_QUESTION }]);

  const handleExtractText = async () => {
    if (!textInput.trim() || textInput.trim().length < 50) {
      toast.error("Please paste at least 50 characters of question paper text");
      return;
    }
    setExtracting(true);
    try {
      const res = await axios.post(`${API}/seeder/upload-text`, null, {
        params: { text: textInput, source_name: sourceName || "Pasted Text" },
      });
      setResult(res.data);
      const sel = {};
      (res.data.questions || []).forEach((q, i) => {
        if (!q.is_duplicate && q.is_relevant !== false) sel[i] = true;
      });
      setSelected(sel);
      toast.success(`Extracted ${res.data.total_found || 0} questions!`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Extraction failed. Check if AI budget is available.");
    } finally {
      setExtracting(false);
    }
  };

  const handleExtractFile = async () => {
    if (!file) { toast.error("Please select a file"); return; }
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/seeder/upload-file?source_name=${encodeURIComponent(sourceName || file.name)}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(res.data);
      const sel = {};
      (res.data.questions || []).forEach((q, i) => {
        if (!q.is_duplicate && q.is_relevant !== false) sel[i] = true;
      });
      setSelected(sel);
      toast.success(`Extracted ${res.data.total_found || 0} questions!`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Extraction failed. Check if AI budget is available.");
    } finally {
      setExtracting(false);
    }
  };

  const handleExtractPair = async () => {
    if (!qpFile || !solFile) { toast.error("Please select both Question Paper and Solution PDFs"); return; }
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append("question_paper", qpFile);
      formData.append("solution_paper", solFile);
      const res = await axios.post(
        `${API}/seeder/upload-pair?source_name=${encodeURIComponent(sourceName || qpFile.name)}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }
      );
      const data = res.data;
      setResult(data);
      const sel = {};
      (data.questions || []).forEach((q, i) => {
        if (!q.is_duplicate && q.is_relevant !== false && q.has_answer && !q.has_truncated_options) sel[i] = true;
      });
      setSelected(sel);
      const withAns = data.with_answers || 0;
      const withExp = data.with_explanations || 0;
      const truncated = data.truncated_options || 0;
      let msg = `Parsed ${data.total_found || 0} questions! ${withAns} with answers, ${withExp} with explanations.`;
      if (truncated > 0) msg += ` ${truncated} have truncated options (auto-deselected).`;
      toast.success(msg);

      // Auto-trigger categorization in background
      if (data.needs_categorization && data.questions?.length > 0) {
        runCategorization(data.questions);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Parsing failed. The PDFs may use an unsupported format.");
    } finally {
      setExtracting(false);
    }
  };

  const [categorizing, setCategorizing] = useState(false);

  const handleExtractCombined = async () => {
    if (!file) { toast.error("Please select a combined PDF"); return; }
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(
        `${API}/seeder/upload-combined?source_name=${encodeURIComponent(sourceName || file.name)}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }
      );
      const data = res.data;
      setResult(data);
      const sel = {};
      (data.questions || []).forEach((q, i) => {
        if (!q.is_duplicate && q.is_relevant !== false && q.has_answer && !q.has_truncated_options) sel[i] = true;
      });
      setSelected(sel);
      const truncated = data.truncated_options || 0;
      let msg = `Parsed ${data.total_found || 0} questions! ${data.with_answers || 0} with answers, ${data.with_explanations || 0} with explanations.`;
      if (truncated > 0) msg += ` ${truncated} have truncated options (auto-deselected).`;
      toast.success(msg);
      if (data.needs_categorization && data.questions?.length > 0) {
        runCategorization(data.questions);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Parsing failed. Make sure the PDF has an 'ANSWERS & EXPLANATIONS' section.");
    } finally {
      setExtracting(false);
    }
  };

  const runCategorization = async (qs) => {
    setCategorizing(true);
    toast.info("Categorizing topics with AI...");
    try {
      // Send only lightweight data (text + number) to avoid payload size issues
      const lightweight = (qs || result?.questions || []).map((q, i) => ({
        question_number: i + 1,
        question_text: q.question_text,
        options: q.options,
        correct_answer: q.correct_answer,
        topic_id: q.topic_id,
        subtopic_id: q.subtopic_id,
        difficulty_level: q.difficulty_level,
      }));
      const catRes = await axios.post(`${API}/seeder/categorize`, lightweight, { timeout: 120000 });
      if (catRes.data.categorized > 0) {
        // Merge AI categories back into full questions
        const catQs = catRes.data.questions;
        setResult(prev => {
          const updated = { ...prev, needs_categorization: false };
          updated.questions = prev.questions.map((q, i) => ({
            ...q,
            topic_id: catQs[i]?.topic_id || q.topic_id,
            subtopic_id: catQs[i]?.subtopic_id || q.subtopic_id,
            difficulty_level: catQs[i]?.difficulty_level || q.difficulty_level,
          }));
          return updated;
        });
        toast.success(`Categorized ${catRes.data.categorized} questions by topic!`);
      } else {
        toast.info("Categorization will happen automatically when you confirm.");
      }
    } catch {
      toast.info("Categorization will happen automatically when you confirm.");
    } finally {
      setCategorizing(false);
    }
  };

  const toggleQuestion = (index) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[index]) delete next[index];
      else next[index] = true;
      return next;
    });
  };

  const selectAll = () => {
    const sel = {};
    (result?.questions || []).forEach((q, i) => {
      if (!q.is_duplicate && q.is_relevant !== false) sel[i] = true;
    });
    setSelected(sel);
  };

  const deselectAll = () => setSelected({});

  const handleConfirm = async () => {
    const selectedQs = (result?.questions || []).filter((_, i) => selected[i]);
    if (selectedQs.length === 0) { toast.error("No questions selected"); return; }
    setConfirming(true);
    try {
      const res = await axios.post(`${API}/seeder/confirm`, {
        questions: selectedQs,
        source_name: sourceName || "Uploaded Paper",
      }, { timeout: 120000 });
      const data = res.data;
      if (data.saved === 0 && data.duplicates > 0) {
        toast.error(`All ${data.duplicates} questions are already in your bank. No new questions added.`);
      } else {
        setConfirmResult(data);
      }
    } catch (err) {
      toast.error("Failed to save questions. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  // Manual entry handlers
  const addManualQuestion = () => {
    setManualQuestions(prev => [...prev, { ...EMPTY_QUESTION }]);
  };

  const updateManualQuestion = (index, updated) => {
    setManualQuestions(prev => prev.map((q, i) => i === index ? updated : q));
  };

  const removeManualQuestion = (index) => {
    if (manualQuestions.length === 1) {
      setManualQuestions([{ ...EMPTY_QUESTION }]);
    } else {
      setManualQuestions(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleManualSubmit = async () => {
    const valid = manualQuestions.filter(q =>
      q.question_text.trim() && q.correct_answer && q.options.A && q.options.B && q.options.C && q.options.D
    );
    if (valid.length === 0) {
      toast.error("Please fill in at least one complete question (text + all options + correct answer)");
      return;
    }
    setConfirming(true);
    try {
      const res = await axios.post(`${API}/seeder/confirm`, {
        questions: valid.map(q => ({ ...q, is_relevant: true })),
        source_name: sourceName || "Manual Entry",
      }, { timeout: 120000 });
      setConfirmResult(res.data);
    } catch (err) {
      toast.error("Failed to save questions");
    } finally {
      setConfirming(false);
    }
  };

  const selectedCount = Object.keys(selected).length;
  const validManualCount = manualQuestions.filter(q =>
    q.question_text.trim() && q.correct_answer && q.options.A && q.options.B
  ).length;

  return (
    <div className="p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="question-seeder-page">
      <div>
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
          Seed Questions
        </h1>
        <p className="text-muted-foreground mt-2 text-base">
          Add questions to your bank — manually, or by uploading coaching papers
        </p>
      </div>

      {!result && (
        <>
          {/* Source Name */}
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Source Name</label>
            <Input
              data-testid="source-name-input"
              placeholder="e.g. Vision IAS PT 365 2025, Shankar IAS Academy Test 12"
              value={sourceName}
              onChange={e => setSourceName(e.target.value)}
              className="bg-card border-border max-w-lg"
            />
          </div>

          {/* Mode Selection */}
          {!mode && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card
                data-testid="mode-pair"
                className="bg-card border-primary/30 cursor-pointer hover:border-primary/60 hover:-translate-y-1 transition-all relative"
                onClick={() => setMode("pair")}
              >
                <div className="absolute -top-2 -right-2">
                  <Badge className="bg-emerald-600 text-white text-[9px] border-0 px-2">Recommended</Badge>
                </div>
                <CardContent className="p-8 text-center">
                  <FilePlus2 size={40} className="mx-auto mb-4 text-emerald-400" />
                  <h3 className="font-heading text-lg font-semibold mb-2">QP + Solution</h3>
                  <p className="text-sm text-muted-foreground">
                    Upload separate Question Paper and Solution PDFs — most accurate, cheapest
                  </p>
                  <Badge className="mt-3 bg-emerald-500/20 text-emerald-400 border-0 text-[10px]">~90% cheaper</Badge>
                </CardContent>
              </Card>
              <Card
                data-testid="mode-manual"
                className="bg-card border-border cursor-pointer hover:border-primary/50 hover:-translate-y-1 transition-all"
                onClick={() => setMode("manual")}
              >
                <CardContent className="p-8 text-center">
                  <PenTool size={40} className="mx-auto mb-4 text-emerald-400" />
                  <h3 className="font-heading text-lg font-semibold mb-2">Manual Entry</h3>
                  <p className="text-sm text-muted-foreground">
                    Add questions one by one with full control over topic, difficulty, and options
                  </p>
                  <Badge className="mt-3 bg-emerald-500/20 text-emerald-400 border-0 text-[10px]">No AI needed</Badge>
                </CardContent>
              </Card>
              <Card
                data-testid="mode-paste-text"
                className="bg-card border-border cursor-pointer hover:border-primary/50 hover:-translate-y-1 transition-all"
                onClick={() => setMode("text")}
              >
                <CardContent className="p-8 text-center">
                  <FileText size={40} className="mx-auto mb-4 text-primary" />
                  <h3 className="font-heading text-lg font-semibold mb-2">Paste Text</h3>
                  <p className="text-sm text-muted-foreground">
                    Paste coaching paper text — AI extracts and categorizes questions
                  </p>
                  <Badge className="mt-3 bg-primary/20 text-primary border-0 text-[10px]">Requires AI budget</Badge>
                </CardContent>
              </Card>
              <Card
                data-testid="mode-upload-file"
                className="bg-card border-border cursor-pointer hover:border-primary/50 hover:-translate-y-1 transition-all"
                onClick={() => setMode("file")}
              >
                <CardContent className="p-8 text-center">
                  <Upload size={40} className="mx-auto mb-4 text-primary" />
                  <h3 className="font-heading text-lg font-semibold mb-2">Upload File</h3>
                  <p className="text-sm text-muted-foreground">
                    Upload PDF, image, or text file — AI processes and extracts MCQs
                  </p>
                  <Badge className="mt-3 bg-primary/20 text-primary border-0 text-[10px]">Requires AI budget</Badge>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Manual Entry Mode */}
          {mode === "manual" && (
            <div className="space-y-4">
              {manualQuestions.map((q, i) => (
                <ManualQuestionForm
                  key={i}
                  question={q}
                  index={i}
                  onChange={updateManualQuestion}
                  onRemove={removeManualQuestion}
                />
              ))}

              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm" onClick={() => setMode(null)} data-testid="back-btn-manual">
                    Back
                  </Button>
                  <Button variant="outline" size="sm" onClick={addManualQuestion} data-testid="add-question-btn" className="gap-1">
                    <Plus size={14} /> Add Question
                  </Button>
                </div>
                <Button
                  data-testid="save-manual-btn"
                  disabled={confirming || validManualCount === 0}
                  onClick={handleManualSubmit}
                  className="bg-primary hover:bg-primary/90 gap-2"
                >
                  {confirming ? (
                    <><Loader2 size={16} className="animate-spin" /> Saving...</>
                  ) : (
                    <><CheckCircle size={16} /> Save {validManualCount} Question{validManualCount !== 1 ? "s" : ""} to Bank</>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* QP + Solution Pair Mode */}
          {mode === "pair" && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <FilePlus2 size={18} className="text-emerald-400" /> Upload Question Paper + Solutions
                </CardTitle>
                {/* Toggle: Separate vs Combined */}
                <div className="flex gap-2 mt-2">
                  <Button
                    variant={pairType === "separate" ? "default" : "outline"}
                    size="sm"
                    data-testid="pair-type-separate"
                    onClick={() => setPairType("separate")}
                    className={pairType === "separate" ? "bg-primary text-primary-foreground" : ""}
                  >
                    Two Separate PDFs
                  </Button>
                  <Button
                    variant={pairType === "combined" ? "default" : "outline"}
                    size="sm"
                    data-testid="pair-type-combined"
                    onClick={() => setPairType("combined")}
                    className={pairType === "combined" ? "bg-primary text-primary-foreground" : ""}
                  >
                    Single Combined PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {pairType === "separate" ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Question Paper PDF</label>
                        <div
                          className="border-2 border-dashed border-border rounded-sm p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                          onClick={() => document.getElementById("qp-file-input").click()}
                          data-testid="qp-dropzone"
                        >
                          {qpFile ? (
                            <div className="space-y-1">
                              <CheckCircle size={24} className="mx-auto text-emerald-400" />
                              <p className="text-sm font-medium truncate">{qpFile.name}</p>
                              <p className="text-xs text-muted-foreground">{(qpFile.size / 1024).toFixed(0)} KB</p>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <FileText size={24} className="mx-auto text-muted-foreground" />
                              <p className="text-xs text-muted-foreground">Click to upload QP</p>
                            </div>
                          )}
                          <input id="qp-file-input" type="file" accept=".pdf" className="hidden" onChange={e => setQpFile(e.target.files?.[0] || null)} />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Solution / Answer Key PDF</label>
                        <div
                          className="border-2 border-dashed border-border rounded-sm p-8 text-center hover:border-emerald-500/50 transition-colors cursor-pointer"
                          onClick={() => document.getElementById("sol-file-input").click()}
                          data-testid="sol-dropzone"
                        >
                          {solFile ? (
                            <div className="space-y-1">
                              <CheckCircle size={24} className="mx-auto text-emerald-400" />
                              <p className="text-sm font-medium truncate">{solFile.name}</p>
                              <p className="text-xs text-muted-foreground">{(solFile.size / 1024).toFixed(0)} KB</p>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <FileText size={24} className="mx-auto text-muted-foreground" />
                              <p className="text-xs text-muted-foreground">Click to upload Solutions</p>
                            </div>
                          )}
                          <input id="sol-file-input" type="file" accept=".pdf" className="hidden" onChange={e => setSolFile(e.target.files?.[0] || null)} />
                        </div>
                      </div>
                    </div>
                    {qpFile && solFile && (
                      <div className="p-3 rounded-sm bg-emerald-500/5 border border-emerald-500/20 text-xs text-emerald-400">
                        Both files selected. Questions will be parsed from the QP and matched with answers from the solution PDF.
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Upload a single PDF that contains both questions and answers/explanations (e.g., "ANSWERS & EXPLANATIONS" section after the questions).
                    </p>
                    <div
                      className="border-2 border-dashed border-border rounded-sm p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                      onClick={() => document.getElementById("combined-file-input").click()}
                      data-testid="combined-dropzone"
                    >
                      {file ? (
                        <div className="space-y-1">
                          <CheckCircle size={24} className="mx-auto text-emerald-400" />
                          <p className="text-sm font-medium truncate">{file.name}</p>
                          <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)} KB</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <FilePlus2 size={24} className="mx-auto text-muted-foreground" />
                          <p className="text-xs text-muted-foreground">Click to upload combined QP + Solution PDF</p>
                        </div>
                      )}
                      <input id="combined-file-input" type="file" accept=".pdf" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} />
                    </div>
                  </>
                )}

                <div className="flex items-center justify-between">
                  <Button variant="outline" size="sm" onClick={() => { setMode(null); setQpFile(null); setSolFile(null); setFile(null); }} data-testid="back-btn-pair">
                    Back
                  </Button>
                  {pairType === "separate" ? (
                    <Button
                      data-testid="extract-pair-btn"
                      disabled={extracting || !qpFile || !solFile}
                      onClick={handleExtractPair}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                    >
                      {extracting ? <><Loader2 size={16} className="animate-spin" /> Parsing...</> : <>Parse &amp; Merge</>}
                    </Button>
                  ) : (
                    <Button
                      data-testid="extract-combined-btn"
                      disabled={extracting || !file}
                      onClick={handleExtractCombined}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                    >
                      {extracting ? <><Loader2 size={16} className="animate-spin" /> Parsing...</> : <>Parse Combined PDF</>}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Text Input Mode */}
          {mode === "text" && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <FileText size={18} className="text-primary" /> Paste Question Paper Text
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  data-testid="paper-text-input"
                  placeholder="Paste the full question paper text here. Include question numbers, options (A/B/C/D), and answer keys if available..."
                  value={textInput}
                  onChange={e => setTextInput(e.target.value)}
                  className="bg-background border-border min-h-[300px] font-mono text-sm"
                />
                <div className="flex items-center justify-between">
                  <Button variant="outline" size="sm" onClick={() => setMode(null)} data-testid="back-btn">
                    Back
                  </Button>
                  <Button
                    data-testid="extract-text-btn"
                    disabled={extracting || textInput.trim().length < 50}
                    onClick={handleExtractText}
                    className="bg-primary hover:bg-primary/90 gap-2"
                  >
                    {extracting ? <><Loader2 size={16} className="animate-spin" /> Extracting...</> : <>Extract Questions</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* File Upload Mode */}
          {mode === "file" && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Upload size={18} className="text-primary" /> Upload Question Paper
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div
                  className="border-2 border-dashed border-border rounded-sm p-12 text-center hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => document.getElementById("file-input").click()}
                  data-testid="file-dropzone"
                >
                  {file ? (
                    <div className="space-y-2">
                      <CheckCircle size={32} className="mx-auto text-emerald-400" />
                      <p className="text-sm font-medium">{file.name}</p>
                      <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload size={32} className="mx-auto text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Click to select a file</p>
                      <p className="text-xs text-muted-foreground">PDF, JPG, PNG, or TXT</p>
                    </div>
                  )}
                  <input
                    id="file-input"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.txt"
                    className="hidden"
                    onChange={e => setFile(e.target.files?.[0] || null)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Button variant="outline" size="sm" onClick={() => { setMode(null); setFile(null); }} data-testid="back-btn-file">
                    Back
                  </Button>
                  <Button
                    data-testid="extract-file-btn"
                    disabled={extracting || !file}
                    onClick={handleExtractFile}
                    className="bg-primary hover:bg-primary/90 gap-2"
                  >
                    {extracting ? <><Loader2 size={16} className="animate-spin" /> Analyzing...</> : <>Extract Questions</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Success State — after confirm */}
      {confirmResult && (
        <Card className="bg-card border-emerald-500/30">
          <CardContent className="p-12 text-center space-y-4">
            <CheckCircle size={48} className="mx-auto text-emerald-400" />
            <h2 className="font-heading text-2xl font-bold">Questions Added Successfully</h2>
            <div className="flex items-center justify-center gap-8 text-sm">
              <div>
                <p className="text-muted-foreground">Saved</p>
                <p className="font-mono text-3xl font-bold text-emerald-400">{confirmResult.saved}</p>
              </div>
              {confirmResult.duplicates > 0 && (
                <div>
                  <p className="text-muted-foreground">Duplicates Skipped</p>
                  <p className="font-mono text-3xl font-bold text-amber-400">{confirmResult.duplicates}</p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground">Total in Bank</p>
                <p className="font-mono text-3xl font-bold">{confirmResult.total_in_bank}</p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 pt-4">
              <Button
                data-testid="seed-more-btn"
                variant="outline"
                onClick={() => { setConfirmResult(null); setResult(null); setSelected({}); setMode(null); setQpFile(null); setSolFile(null); setFile(null); setTextInput(""); }}
              >
                Seed More Questions
              </Button>
              <Button
                data-testid="goto-topics-btn"
                onClick={() => window.location.href = "/topics"}
                className="bg-primary hover:bg-primary/90"
              >
                View Topic Map
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Extraction Results (AI modes) */}
      {result && !confirmResult && (
        <div className="space-y-6">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Found</p>
                    <p className="font-mono text-2xl font-bold">{result.total_found || 0}</p>
                  </div>
                  {result.with_answers !== undefined && (
                    <div>
                      <p className="text-xs text-emerald-400 uppercase tracking-wider">With Answers</p>
                      <p className="font-mono text-2xl font-bold text-emerald-400">{result.with_answers}</p>
                    </div>
                  )}
                  {result.with_explanations !== undefined && (
                    <div>
                      <p className="text-xs text-blue-400 uppercase tracking-wider">With Explanations</p>
                      <p className="font-mono text-2xl font-bold text-blue-400">{result.with_explanations}</p>
                    </div>
                  )}
                  {result.relevant_count !== undefined && (
                    <div>
                      <p className="text-xs text-emerald-400 uppercase tracking-wider">UPSC Level</p>
                      <p className="font-mono text-2xl font-bold text-emerald-400">{result.relevant_count}</p>
                    </div>
                  )}
                  {result.irrelevant_count > 0 && (
                    <div>
                      <p className="text-xs text-red-400 uppercase tracking-wider">Rejected</p>
                      <p className="font-mono text-2xl font-bold text-red-400">{result.irrelevant_count}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground">Selected</p>
                    <p className="font-mono text-2xl font-bold text-primary">{selectedCount}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">Select Relevant</Button>
                  <Button variant="outline" size="sm" onClick={deselectAll} data-testid="deselect-all-btn">Deselect All</Button>
                </div>
              </div>
              {result.source_analysis && (
                <p className="text-xs text-muted-foreground mt-3 border-t border-border pt-3">{result.source_analysis}</p>
              )}
              {result.merge_stats && (
                <div className="text-xs text-muted-foreground mt-2 flex flex-wrap gap-3">
                  {result.merge_stats.unmatched_questions?.length > 0 && (
                    <span className="text-amber-400">
                      {result.merge_stats.unmatched_questions.length} questions without matching answers (Q: {result.merge_stats.unmatched_questions.slice(0, 10).join(", ")})
                    </span>
                  )}
                  {result.parsing_method && (
                    <span>Method: {result.parsing_method}</span>
                  )}
                </div>
              )}
              {result.irrelevant_count > 0 && (
                <p className="text-xs text-amber-400 mt-2">
                  {result.irrelevant_count} questions filtered out as below UPSC standard (textbook/basic level). They appear greyed out below.
                </p>
              )}
            </CardContent>
          </Card>

          {/* UPSC-Level Questions */}
          {(() => {
            const allQs = result.questions || [];
            const relevantQs = allQs.filter(q => q.is_relevant !== false);
            const dupeCount = relevantQs.filter(q => q.is_duplicate).length;
            const allDupes = dupeCount === relevantQs.length && relevantQs.length > 0;
            return (
              <>
                {allDupes && (
                  <div className="p-4 rounded-sm bg-amber-500/10 border border-amber-500/30 text-sm text-amber-300 space-y-1" data-testid="all-dupes-banner">
                    <p className="font-semibold">All {relevantQs.length} questions are already in your bank.</p>
                    <p className="text-xs text-amber-400/70">Upload a different paper to add new questions.</p>
                  </div>
                )}
                {relevantQs.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-emerald-400 uppercase tracking-wider">
                      UPSC-Level Questions
                      {dupeCount > 0 && !allDupes && <span className="text-amber-400 ml-2">({dupeCount} already in bank)</span>}
                    </h3>
                    {relevantQs.map((q) => {
                      const origIndex = allQs.indexOf(q);
                      return (
                        <ExtractedQuestion key={origIndex} q={q} index={origIndex} onToggle={toggleQuestion} selected={!!selected[origIndex]} />
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}

          {/* Rejected Questions */}
          {(result.questions || []).some(q => q.is_relevant === false) && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-red-400/70 uppercase tracking-wider">
                Rejected — Below UPSC Standard ({(result.questions || []).filter(q => q.is_relevant === false).length})
              </h3>
              {(result.questions || []).filter(q => q.is_relevant === false).map((q, i) => {
                const origIndex = (result.questions || []).indexOf(q);
                return (
                  <ExtractedQuestion key={origIndex} q={q} index={origIndex} onToggle={toggleQuestion} selected={!!selected[origIndex]} />
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-border sticky bottom-0 bg-background py-4 pb-24 z-10">
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => { setResult(null); setSelected({}); setQpFile(null); setSolFile(null); }} data-testid="discard-btn">
                <Trash2 size={14} className="mr-1" /> Discard
              </Button>
              {result?.needs_categorization && (
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="categorize-btn"
                  disabled={categorizing}
                  onClick={() => runCategorization()}
                  className="text-xs gap-1 border-primary/30 text-primary"
                >
                  {categorizing ? <><Loader2 size={12} className="animate-spin" /> Categorizing...</> : "Categorize Topics (AI)"}
                </Button>
              )}
            </div>
            <Button
              data-testid="confirm-add-btn"
              size="lg"
              disabled={confirming || selectedCount === 0}
              onClick={handleConfirm}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-base px-8 py-3 gap-2 font-semibold disabled:opacity-50"
            >
              {confirming ? (
                <><Loader2 size={18} className="animate-spin" /> Adding to Bank...</>
              ) : selectedCount === 0 ? (
                "No New Questions to Add"
              ) : (
                <><Plus size={18} /> Add {selectedCount} Questions to Bank</>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
